import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export interface IncomingCall {
  id: string;
  call_id: string;
  conversation_id: string | null;
  channel_id: string | null;
  contact_id: string | null;
  from_number: string | null;
  to_number: string | null;
  sdp_offer: string | null;
  sdp_answer: string | null;
  phone_number_id: string | null;
  status: string;
  direction: string;
  started_at: string | null;
  contact_name?: string;
}

async function enrichContactName(call: any): Promise<IncomingCall> {
  let contactName = call.from_number;
  if (call.contact_id) {
    const { data: contact } = await supabase
      .from("contacts")
      .select("name")
      .eq("id", call.contact_id)
      .maybeSingle();
    if (contact?.name) contactName = contact.name;
  }
  return { ...call, contact_name: contactName } as IncomingCall;
}

export function useIncomingCalls() {
  const [activeCall, setActiveCall] = useState<IncomingCall | null>(null);
  const [callQueue, setCallQueue] = useState<IncomingCall[]>([]);
  const [callStatus, setCallStatus] = useState<string>("idle");
  const onSdpAnswerRef = useRef<((sdp: string) => void) | null>(null);
  const { activeWorkspaceId } = useAuth();

  const enqueueOrSetActive = useCallback(
    (call: IncomingCall) => {
      setActiveCall((prev) => {
        if (!prev) {
          setCallStatus("ringing");
          return call;
        }
        // Already have an active call → queue it
        setCallQueue((q) => [...q, call]);
        return prev;
      });
    },
    []
  );

  const promoteNextFromQueue = useCallback(() => {
    setCallQueue((q) => {
      if (q.length === 0) {
        setActiveCall(null);
        setCallStatus("idle");
        return q;
      }
      const [next, ...rest] = q;
      setActiveCall(next);
      setCallStatus("ringing");
      return rest;
    });
  }, []);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    const checkExisting = async () => {
      const { data } = await supabase
        .from("calls")
        .select("*")
        .eq("organization_id", activeWorkspaceId)
        .eq("status", "ringing")
        .eq("direction", "inbound")
        .order("created_at", { ascending: true })
        .limit(5);
      if (data) {
        for (const raw of data) {
          if (!raw.sdp_offer) continue;
          const call = await enrichContactName(raw);
          enqueueOrSetActive(call);
        }
      }
    };
    checkExisting();

    const channel = supabase
      .channel("incoming-calls")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "calls" },
        async (payload) => {
          const raw = payload.new as any;
          if (raw.status === "ringing" && raw.direction === "inbound" && raw.sdp_offer) {
            const call = await enrichContactName(raw);
            enqueueOrSetActive(call);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "calls" },
        (payload) => {
          const updated = payload.new as any;

          // Outbound call answered → SDP answer
          if (updated.sdp_answer && updated.direction === "outbound" && updated.status === "answered") {
            setActiveCall((prev) => {
              if (prev && prev.id === updated.id) {
                onSdpAnswerRef.current?.(updated.sdp_answer);
                return { ...prev, sdp_answer: updated.sdp_answer, status: "answered" };
              }
              return prev;
            });
            setCallStatus("in_call");
            return;
          }

          // Call ended
          if (["completed", "missed", "rejected", "failed"].includes(updated.status)) {
            setActiveCall((prev) => {
              if (prev && prev.id === updated.id) {
                setCallStatus("ended");
                setTimeout(() => promoteNextFromQueue(), 2000);
              }
              return prev;
            });
            // Also remove from queue if it was queued
            setCallQueue((q) => q.filter((c) => c.id !== updated.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enqueueOrSetActive, promoteNextFromQueue, activeWorkspaceId]);

  const acceptCall = useCallback(
    async (sdpAnswer: string) => {
      if (!activeCall) return;
      setCallStatus("connecting");
      try {
        const { error: preErr } = await supabase.functions.invoke("whatsapp-call-control", {
          body: { action: "pre_accept", call_db_id: activeCall.id, sdp_answer: sdpAnswer },
        });
        if (preErr) throw preErr;
        const { error: accErr } = await supabase.functions.invoke("whatsapp-call-control", {
          body: { action: "accept", call_db_id: activeCall.id },
        });
        if (accErr) throw accErr;
        setCallStatus("in_call");
      } catch (err) {
        console.error("Accept call error:", err);
        setCallStatus("failed");
      }
    },
    [activeCall]
  );

  const initiateCall = useCallback(
    async (params: {
      channelId: string;
      toPhone: string;
      conversationId?: string;
      contactId?: string;
      contactName?: string;
    }) => {
      try {
        setCallStatus("initiating");

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        });
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const sdpOffer = await new Promise<string>((resolve) => {
          const timeout = setTimeout(() => resolve(pc.localDescription?.sdp || offer.sdp || ""), 3000);
          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === "complete") {
              clearTimeout(timeout);
              resolve(pc.localDescription?.sdp || offer.sdp || "");
            }
          };
        });

        onSdpAnswerRef.current = async (remoteSdp: string) => {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: remoteSdp }));
          } catch (err) {
            console.error("Error setting remote SDP:", err);
          }
        };

        pc.ontrack = (event) => {
          const audio = document.getElementById("remote-call-audio") as HTMLAudioElement;
          if (audio) {
            audio.srcObject = event.streams[0];
            audio.play().catch(console.error);
          }
        };

        const { data, error } = await supabase.functions.invoke("whatsapp-call-control", {
          body: {
            action: "initiate",
            channel_id: params.channelId,
            to_phone: params.toPhone,
            sdp_offer: sdpOffer,
            conversation_id: params.conversationId,
            contact_id: params.contactId,
          },
        });

        if (error) throw error;
        const result = typeof data === "string" ? JSON.parse(data) : data;
        if (!result.success) throw new Error(result.error || "Failed to initiate call");

        setActiveCall({
          id: result.call_db_id,
          call_id: result.call_id,
          conversation_id: params.conversationId || null,
          channel_id: params.channelId,
          contact_id: params.contactId || null,
          from_number: null,
          to_number: params.toPhone,
          sdp_offer: sdpOffer,
          sdp_answer: null,
          phone_number_id: null,
          status: "calling",
          direction: "outbound",
          started_at: new Date().toISOString(),
          contact_name: params.contactName || params.toPhone,
          _pc: pc,
          _stream: stream,
        } as any);
        setCallStatus("calling");
      } catch (err: any) {
        console.error("Initiate call error:", err);
        setCallStatus("idle");
        throw err;
      }
    },
    []
  );

  const rejectCall = useCallback(async () => {
    if (!activeCall) return;
    try {
      await supabase.functions.invoke("whatsapp-call-control", {
        body: { action: "reject", call_db_id: activeCall.id },
      });
    } catch (err) {
      console.error("Reject call error:", err);
    }
    const callAny = activeCall as any;
    callAny._pc?.close();
    callAny._stream?.getTracks().forEach((t: any) => t.stop());
    onSdpAnswerRef.current = null;
    promoteNextFromQueue();
  }, [activeCall, promoteNextFromQueue]);

  const endCall = useCallback(async () => {
    if (!activeCall) return;
    try {
      await supabase.functions.invoke("whatsapp-call-control", {
        body: { action: "terminate", call_db_id: activeCall.id },
      });
    } catch (err) {
      console.error("End call error:", err);
    }
    const callAny = activeCall as any;
    callAny._pc?.close();
    callAny._stream?.getTracks().forEach((t: any) => t.stop());
    onSdpAnswerRef.current = null;
    promoteNextFromQueue();
  }, [activeCall, promoteNextFromQueue]);

  const dismissCall = useCallback(() => {
    const callAny = activeCall as any;
    callAny?._pc?.close();
    callAny?._stream?.getTracks().forEach((t: any) => t.stop());
    onSdpAnswerRef.current = null;
    promoteNextFromQueue();
  }, [activeCall, promoteNextFromQueue]);

  return {
    incomingCall: activeCall,
    callStatus,
    callQueueCount: callQueue.length,
    acceptCall,
    rejectCall,
    endCall,
    dismissCall,
    initiateCall,
  };
}
